<template>
  <i-panel
    :visible="isShowable"
    class="view-collect-session-modal"
    @hide-panel="hidePanel"
  >
    <template slot="header">
      <div class="d-flex w-100">
        <div class="w-100">
          <h4 class="i-text-heading-4 i-text-neutral-120 i-mb-2 modal-heading">
            {{ selectedSession.customer_name }}
          </h4>
          <p class="i-text-body-2 i-text-neutral-110">
            Collect session details
          </p>
        </div>
      </div>
    </template>

    <div id="collect-session-detail" class="d-flex flex-column">
      <div>
        <h5 class="i-text-heading-6">Session Link</h5>
        <i-copy
          element-id="session-url"
          :value="selectedSession.url"
          container="collect-session-detail"
        ></i-copy>
      </div>

      <div
        v-if="selectedSession.customer_information_requests?.length > 0"
        class="i-mt-11"
      >
        <h5 class="i-text-heading-6">Information</h5>
        <info-request
          v-for="(
            infoItem, index
          ) in selectedSession.customer_information_requests"
          :key="infoItem.id + index"
          :type="infoItem.name"
          :response="infoItem.responses"
          class="i-mb-4"
        />
      </div>

      <div
        v-if="!loadingDocuments && hasDocumentRequest && sessionDocuments"
        class="i-mt-11"
      >
        <h5 class="i-text-heading-6 i-mb-6">Documents</h5>
        <div
          v-for="(docRequest, rIndex) in sessionDocuments.document_requests"
          :key="docRequest.id + rIndex"
          class="i-mb-8"
        >
          <h6 class="i-text-subheading-2 i-text-neutral-120 i-mb-6">
            {{ docRequest.label }}
          </h6>
          <div v-if="docRequest.documents.length > 0" class="docs-list">
            <document-item
              v-for="(docItem, dIndex) in docRequest.documents"
              :key="docItem + dIndex"
              :document="docItem"
              data-testid="document-switcher-item"
              size="small"
              :options="{
                actionMenu: false,
                checks: false,
                currentDocLabel: false,
                tags: false,
                size: 'small',
                trustScoreInfo: true,
                defaultNumSignalsVisible: 0,
                clickable: false,
              }"
            />
          </div>

          <div v-else>
            <p class="i-text-body-3 i-text-neutral-110">
              No documents have been uploaded yet
            </p>
          </div>
        </div>
      </div>

      <div v-else-if="loadingDocuments">
        <i-spinner size="xl" class="i-mt-11"></i-spinner>
      </div>

      <div
        v-else-if="
          hasDocumentRequest && !loadingDocuments && documentsLoadError
        "
        class="i-mt-11"
      >
        <!-- This is a fallback in the event that a single session cannot be fetched due to a HTTP error -->
        <h5 class="i-text-heading-6 i-mb-6">Documents</h5>

        <div
          v-for="(request, sDRIndex) in selectedSession.document_requests"
          :key="request.id + sDRIndex"
          class="i-mb-8"
        >
          <h6 class="i-text-subheading-2 i-text-neutral-120 i-mb-6">
            {{ request.label }}
          </h6>

          <div v-if="request.uploads.length > 0">
            <document-item-lite
              v-for="(upload, index) in request.uploads"
              :key="upload.filename + index"
              :filename="upload.filename"
              :thumbnail="upload.thumbnail"
            ></document-item-lite>
          </div>
          <div v-else>
            <p class="i-text-body-3 i-text-neutral-110">
              No documents have been uploaded yet
            </p>
          </div>
        </div>
      </div>
    </div>

    <template slot="footer">
      <i-button
        text="View Customer"
        class="w-100"
        type="neutral"
        subtype="subtle"
        size="lg"
        @button-click="viewCustomer(selectedSession.customer_id)"
      ></i-button>
    </template>
  </i-panel>
</template>

<script>
import { iButton, iCopyInput, iPanel, iSpinner } from '@inscribe/signal';
import DocumentItem from '@/V2/documents/document-list/DocumentItem';
import DocumentItemLite from '@/V2/collect/DocumentItemLite';
import InfoRequest from '@/V2/collect/InformationRequestItem';
import { mapActions } from 'vuex';

export default {
  components: {
    iButton,
    iPanel,
    iSpinner,
    DocumentItem,
    DocumentItemLite,
    InfoRequest,
    'i-copy': iCopyInput,
  },
  props: {
    selectedSession: {
      type: Object,
      required: true,
    },
    isShowable: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      sessionDocuments: null,
      loadingDocuments: false,
      documentsLoadError: false,
    };
  },
  computed: {
    hasDocumentRequest() {
      return (
        this.selectedSession.document_requests &&
        this.selectedSession.document_requests.length > 0
      );
    },
  },
  mounted() {
    if (this.hasDocumentRequest) {
      this.loadingDocuments = true;
      const loadSession = this.getSingleSession(this.selectedSession.id);

      loadSession
        .then((session) => {
          this.sessionDocuments = session.data;
          this.loadingDocuments = false;
        })
        .catch(() => {
          this.loadingDocuments = false;
          this.documentsLoadError = true;
        });
    }
  },
  methods: {
    ...mapActions({
      getSingleSession: 'getSingleSession',
    }),

    hidePanel() {
      this.sessionDocuments = null;
      this.$emit('hide-view-modal');
    },

    viewCustomer(customerId) {
      this.sessionDocuments = null;
      this.$emit('hide-view-modal');
      this.$router.push({
        name: 'customer-overview',
        params: {
          customer_id: customerId,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.view-collect-session-modal {
  .modal-heading {
    width: 420px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .docs-list {
    border: 1px solid $light-neutral-60;
    border-radius: 16px;
  }
}
</style>
